/**
 * ClaimVouchersCard - the claim's vouchers on its Settlement tab.
 *
 * Status, number, expiry and redemption per voucher, plus payments still
 * waiting for one. Which buttons show comes from the server (`actions`), so
 * the lifecycle and the caller's permissions are never re-derived here.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Ticket } from 'lucide-react'
import { toast } from 'sonner'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import {
  useClaimVouchers,
  useMintVoucher,
  useRedeemVoucher,
  useVoucherAction,
  type AwaitingVoucherPayment,
  type ClaimVoucher,
  type VoucherAction,
  type VoucherField,
} from '@/admin/hooks/use-vouchers'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'
import {
  genFormFields,
  normaliseTemplateAttribute,
  useForm,
  type FormField,
} from '@/shared/lib/forms'

/** A claim field and its value, used to prefill a redemption by code name. */
export interface SupplierDefault {
  code_name: string
  value: unknown
}

interface ClaimVouchersCardProps {
  claimUuid: string
  className?: string
  /** The claim's Supplier card, in card order; prefills a redemption. */
  supplierDefaults?: SupplierDefault[]
}

type Pending =
  | { kind: 'voucher'; voucher: ClaimVoucher; action: VoucherAction }
  | { kind: 'mint'; payment: AwaitingVoucherPayment }

export function ClaimVouchersCard({
  claimUuid,
  className,
  supplierDefaults = [],
}: ClaimVouchersCardProps) {
  const { t } = useTranslation()
  const { formatDate, formatCurrency } = useFormatters()
  const { data, isPending, isFetching, isError, refetch } =
    useClaimVouchers(claimUuid)
  const [pending, setPending] = useState<Pending | null>(null)

  const vouchers = useMemo(() => data?.vouchers ?? [], [data])
  const awaiting = data?.awaiting ?? []

  const actionLabel: Record<VoucherAction, string> = {
    issue: t('claims.vouchers.issue', 'Issue'),
    resend: t('claims.vouchers.resend', 'Resend'),
    number: t('claims.vouchers.enterNumber', 'Enter number'),
    cancel: t('claims.vouchers.cancel', 'Cancel'),
    reactivate: t('claims.vouchers.reactivate', 'Reactivate'),
    redeem: t('claims.vouchers.redeem', 'Redeem'),
  }

  const money = (amount: string | null) =>
    amount === null ? '-' : formatCurrency(Number(amount))
  // No pinned, empty actions column once nothing is left to do.
  const hasActions = vouchers.some((v) => v.actions.length > 0)

  const columns = useMemo<DataTableColumn<ClaimVoucher>[]>(
    () => [
      {
        id: 'number',
        header: t('claims.vouchers.number', 'Number'),
        cell: (row) => (
          <span className="font-mono text-[12px] text-[var(--ink)]">
            {row.number ??
              t('claims.vouchers.awaitingNumber', 'Awaiting number')}
          </span>
        ),
      },
      {
        id: 'program',
        header: t('claims.vouchers.program', 'Program'),
        cell: (row) => (
          <span className="text-[var(--ink-2)]">{row.program.label}</span>
        ),
      },
      {
        id: 'created',
        header: t('claims.vouchers.created', 'Created'),
        cell: (row) => (row.created_at ? formatDate(row.created_at) : '-'),
      },
      {
        id: 'amount',
        header: t('claims.vouchers.amount', 'Amount'),
        align: 'right',
        cell: (row) => (
          <span className="tabular-nums">{money(row.amount)}</span>
        ),
      },
      {
        id: 'status',
        header: t('claims.vouchers.status', 'Status'),
        cell: (row) => (
          <StatusBadge
            status={row.state.label}
            color={row.state.color || undefined}
          />
        ),
      },
      {
        id: 'expires',
        header: t('claims.vouchers.expires', 'Expires'),
        cell: (row) =>
          row.expires_on
            ? formatDate(row.expires_on)
            : t('claims.vouchers.neverExpires', 'Never'),
      },
      {
        id: 'redeemer',
        header: t('claims.vouchers.redeemedBy', 'Redeemed by'),
        cell: (row) => row.redeemer?.name || '-',
      },
      {
        id: 'confirmation',
        header: t('claims.vouchers.confirmation', 'Confirmation'),
        cell: (row) => (
          <span className="font-mono text-[12px]">
            {row.confirmation_number || '-'}
          </span>
        ),
      },
      // WPS-244: what the voucher's payment settled under, and who recorded it.
      {
        id: 'payment_reference',
        header: t('claims.vouchers.paymentReference', 'Payment reference'),
        cell: (row) =>
          row.payment_reference?.number ? (
            <div className="flex flex-col">
              <span className="font-mono text-[12px]">
                {row.payment_reference.number}
              </span>
              {row.payment_reference.origin ? (
                <span className="text-[11px] text-[var(--ink-3)]">
                  {row.payment_reference.origin}
                </span>
              ) : null}
            </div>
          ) : (
            '-'
          ),
      },
      ...(hasActions
        ? [
            {
              id: 'actions',
              header: '',
              align: 'right',
              sticky: 'right',
              cell: (row: ClaimVoucher) => (
                <div className="flex items-center justify-end gap-1">
                  {row.actions.map((action) => (
                    <Button
                      key={action}
                      type="button"
                      size="xs"
                      variant={action === 'cancel' ? 'ghost' : 'outline'}
                      data-testid={`voucher-${action}-${row.id}`}
                      onClick={() =>
                        setPending({ kind: 'voucher', voucher: row, action })
                      }
                    >
                      {actionLabel[action]}
                    </Button>
                  ))}
                </div>
              ),
            } satisfies DataTableColumn<ClaimVoucher>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, formatDate, formatCurrency, hasActions],
  )

  // A claim no voucher program touches shows nothing, once that is known.
  if (
    !isPending &&
    !isError &&
    vouchers.length === 0 &&
    awaiting.length === 0
  ) {
    return null
  }

  return (
    <div className={className}>
      <DetailCard
        title={t('claims.vouchers.title', 'Vouchers')}
        icon={<Ticket className="h-3.5 w-3.5" />}
        collapsible={false}
      >
        <div className="flex flex-col" data-testid="claim-vouchers-card">
          <DataTable<ClaimVoucher>
            tableId="claim-vouchers"
            columns={columns}
            data={vouchers}
            totalCount={vouchers.length}
            isLoading={isPending}
            isFetching={isFetching}
            isError={isError}
            onRetry={() => void refetch()}
            rowKey="id"
            rowTestId={(row) => `voucher-row-${row.id}`}
            emptyMessage={t('claims.vouchers.none', 'No vouchers yet.')}
          />
          {awaiting.map((payment) => (
            <div
              key={payment.payment_id}
              className="flex items-center justify-between gap-4 border-t-[0.5px] border-[var(--rule-light)] px-4 py-3 text-[12.5px] text-[var(--ink-2)]"
              data-testid={`voucher-awaiting-${payment.payment_id}`}
            >
              <span>
                {t('claims.vouchers.awaiting', {
                  defaultValue:
                    'A {{amount}} {{program}} payment has no voucher yet.',
                  amount: money(payment.amount),
                  program: payment.program?.label ?? '',
                })}
              </span>
              {payment.actions.includes('mint') && (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  data-testid={`voucher-mint-${payment.payment_id}`}
                  onClick={() => setPending({ kind: 'mint', payment })}
                >
                  {t('claims.vouchers.mint', 'Mint voucher')}
                </Button>
              )}
            </div>
          ))}
        </div>
        {pending && (
          <VoucherActionDialog
            claimUuid={claimUuid}
            pending={pending}
            fields={data?.fields ?? []}
            supplierDefaults={supplierDefaults}
            onClose={() => setPending(null)}
          />
        )}
      </DetailCard>
    </div>
  )
}

function VoucherActionDialog({
  claimUuid,
  pending,
  fields,
  supplierDefaults,
  onClose,
}: {
  claimUuid: string
  pending: Pending
  fields: VoucherField[]
  supplierDefaults: SupplierDefault[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const act = useVoucherAction(claimUuid)
  const mint = useMintVoucher(claimUuid)
  const redeem = useRedeemVoucher(claimUuid)
  const [confirmed, setConfirmed] = useState<{
    confirmation: string
    number: string
    supplier: string
  } | null>(null)
  const failed = t(
    'claims.vouchers.failed',
    'The voucher could not be updated.',
  )

  const attempt = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work()
      toast.success(done)
      onClose()
    } catch (err) {
      toastApiError(err, failed)
    }
  }

  if (pending.kind === 'mint') {
    return (
      <ConfirmationDialog
        open
        onOpenChange={(open) => (open ? null : onClose())}
        title={t(
          'claims.vouchers.mintTitle',
          'Mint a voucher for this payment?',
        )}
        description={t(
          'claims.vouchers.mintBody',
          'A new voucher is made for the payment and holds it until it is redeemed.',
        )}
        confirmLabel={t('claims.vouchers.mint', 'Mint voucher')}
        isPending={mint.isPending}
        onConfirm={() =>
          attempt(
            () => mint.mutateAsync(pending.payment.payment_id),
            t('claims.vouchers.minted', 'Voucher minted.'),
          )
        }
      />
    )
  }

  const { voucher, action } = pending
  const number = voucher.number ?? ''
  const run = (body: Record<string, unknown>, done: string) =>
    attempt(
      () =>
        act.mutateAsync({
          voucherId: voucher.id,
          action: action as Exclude<VoucherAction, 'redeem'>,
          body,
        }),
      done,
    )

  if (action === 'issue') {
    const current = voucher.expires_on ?? ''
    return (
      <VoucherFormDialog
        formId="voucher-issue-form"
        title={t('claims.vouchers.issueTitle', 'Issue this voucher?')}
        description={t('claims.vouchers.issueBody', {
          defaultValue: 'Voucher {{number}} is sent to its holder.',
          number,
        })}
        fields={[
          {
            type: 'datepicker',
            name: 'expires_on',
            label: t('claims.vouchers.lastDay', 'Last day'),
            value: current,
            disablePast: true,
            hint: t(
              'claims.vouchers.lastDayHint',
              "Leave it as it is to keep the program's term.",
            ),
          },
        ]}
        submitLabel={t('claims.vouchers.issue', 'Issue')}
        isPending={act.isPending}
        onClose={onClose}
        onSubmit={(values) => {
          const chosen = String(values.expires_on ?? '')
          return run(
            chosen && chosen !== current ? { expires_on: chosen } : {},
            t('claims.vouchers.issued', 'Voucher issued.'),
          )
        }}
      />
    )
  }

  if (action === 'resend') {
    return (
      <ConfirmationDialog
        open
        onOpenChange={(open) => (open ? null : onClose())}
        title={t('claims.vouchers.resendTitle', 'Send this voucher again?')}
        description={t('claims.vouchers.issueBody', {
          defaultValue: 'Voucher {{number}} is sent to its holder.',
          number,
        })}
        confirmLabel={t('claims.vouchers.resend', 'Resend')}
        isPending={act.isPending}
        onConfirm={() =>
          run({}, t('claims.vouchers.resent', 'Voucher sent again.'))
        }
      />
    )
  }

  if (action === 'cancel') {
    return (
      <VoucherFormDialog
        formId="voucher-cancel-form"
        title={t('claims.vouchers.cancelTitle', 'Cancel this voucher?')}
        description={t('claims.vouchers.cancelBody', {
          defaultValue:
            'Voucher {{number}} can no longer be redeemed. Its payment waits for a new voucher.',
          number,
        })}
        fields={[
          {
            type: 'textarea',
            name: 'reason',
            label: t('claims.vouchers.reason', 'Reason'),
            value: '',
            required: t(
              'claims.vouchers.reasonRequired',
              'Say why the voucher is being cancelled.',
            ),
          },
        ]}
        submitLabel={t('claims.vouchers.cancelConfirm', 'Cancel voucher')}
        destructive
        isPending={act.isPending}
        onClose={onClose}
        onSubmit={(values) =>
          run(
            { reason: String(values.reason ?? '').trim() },
            t('claims.vouchers.cancelled', 'Voucher cancelled.'),
          )
        }
      />
    )
  }

  if (action === 'reactivate') {
    return (
      <VoucherFormDialog
        formId="voucher-reactivate-form"
        title={t('claims.vouchers.reactivateTitle', 'Reactivate this voucher?')}
        description={t('claims.vouchers.reactivateBody', {
          defaultValue:
            'Voucher {{number}} can be redeemed again under the same number. Leave the date empty to use the program term from today.',
          number,
        })}
        fields={[
          {
            type: 'datepicker',
            name: 'expires_on',
            label: t('claims.vouchers.newLastDay', 'New last day'),
            value: '',
            disablePast: true,
          },
        ]}
        submitLabel={t('claims.vouchers.reactivate', 'Reactivate')}
        isPending={act.isPending}
        onClose={onClose}
        onSubmit={(values) =>
          run(
            values.expires_on ? { expires_on: String(values.expires_on) } : {},
            t('claims.vouchers.reactivated', 'Voucher reactivated.'),
          )
        }
      />
    )
  }

  if (action === 'number') {
    return (
      <VoucherFormDialog
        formId="voucher-number-form"
        title={t('claims.vouchers.numberTitle', 'Enter the voucher number')}
        description={t(
          'claims.vouchers.numberBody',
          'This program takes its numbers from another system. A number can be set once.',
        )}
        fields={[
          {
            type: 'text',
            name: 'number',
            label: t('claims.vouchers.number', 'Number'),
            value: '',
            required: t(
              'claims.vouchers.numberRequired',
              'Enter the voucher number.',
            ),
          },
        ]}
        submitLabel={t('common.save', 'Save')}
        isPending={act.isPending}
        onClose={onClose}
        onSubmit={(values) =>
          run(
            { number: String(values.number ?? '').trim() },
            t('claims.vouchers.numbered', 'Voucher number saved.'),
          )
        }
      />
    )
  }

  if (confirmed) {
    return (
      <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('claims.vouchers.confirmationTitle', 'Confirmation number')}
            </DialogTitle>
            <DialogDescription>
              {t('claims.vouchers.confirmationBody', {
                defaultValue:
                  'Voucher {{number}} is redeemed. Give this confirmation number to {{supplier}}.',
                number: confirmed.number,
                supplier: confirmed.supplier,
              })}
            </DialogDescription>
          </DialogHeader>
          <p
            className="py-3 text-center font-mono text-[22px] tracking-[0.08em] text-[var(--ink)]"
            data-testid="voucher-confirmation-number"
          >
            {confirmed.confirmation}
          </p>
          <DialogFooter>
            <Button onClick={onClose} data-testid="voucher-confirmation-close">
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Prefilled from the claim's Supplier card: its first field names the
  // supplier, and a voucher field takes the claim field of the same name.
  const defaults = new Map(supplierDefaults.map((d) => [d.code_name, d.value]))
  const text = (value: unknown) =>
    value === null || value === undefined ? '' : String(value)
  const voucherFields: FormField[] = fields.map((field) => ({
    ...genFormFields(normaliseTemplateAttribute(field)),
    value: text(defaults.get(field.code_name)),
  }))
  return (
    <VoucherFormDialog
      formId="voucher-redeem-form"
      title={t('claims.vouchers.redeemTitle', 'Redeem this voucher')}
      description={t('claims.vouchers.redeemCheckBody', {
        defaultValue:
          'Enter the voucher number the supplier quotes. It must belong to claim {{claim}}, and a voucher is redeemed once.',
        claim: voucher.claim.reference_number,
      })}
      fields={[
        {
          type: 'text',
          name: 'number',
          label: t('claims.vouchers.voucherNumber', 'Voucher number'),
          value: '',
          required: t(
            'claims.vouchers.voucherNumberRequired',
            'Enter the voucher number the supplier quotes.',
          ),
        },
        {
          type: 'text',
          name: 'redeemer',
          label: t('claims.vouchers.supplier', 'Supplier'),
          placeholder: t('claims.vouchers.supplierHint', 'Who is redeeming it'),
          value: text(supplierDefaults[0]?.value),
          required: t(
            'claims.vouchers.supplierRequired',
            'Say which supplier is redeeming the voucher.',
          ),
        },
        ...voucherFields,
      ]}
      submitLabel={t(
        'claims.vouchers.generateConfirmation',
        'Generate confirmation number',
      )}
      isPending={redeem.isPending}
      onClose={onClose}
      onSubmit={async (values) => {
        const supplier = String(values.redeemer ?? '').trim()
        try {
          const redeemed = await redeem.mutateAsync({
            number: String(values.number ?? '').trim(),
            // The reference the server matches on, even for an unnumbered claim.
            claim_number: voucher.claim.reference_number,
            redeemer: { name: supplier },
            // Blank optional fields are left out rather than sent empty.
            attributes: Object.fromEntries(
              fields
                .map((field) => [field.code_name, values[field.code_name]])
                .filter(([, value]) => text(value).trim() !== ''),
            ),
          })
          setConfirmed({
            confirmation: redeemed.confirmation_number ?? '',
            number: redeemed.number ?? '',
            supplier,
          })
        } catch (err) {
          toastApiError(err, failed)
        }
      }}
    />
  )
}

function VoucherFormDialog({
  formId,
  title,
  description,
  fields,
  submitLabel,
  destructive = false,
  isPending,
  onClose,
  onSubmit,
}: {
  formId: string
  title: string
  description: string
  fields: FormField[]
  submitLabel: string
  destructive?: boolean
  isPending: boolean
  onClose: () => void
  onSubmit: (values: Record<string, unknown>) => Promise<unknown>
}) {
  const { t } = useTranslation()
  const { Form } = useForm(
    { name: formId, submit: 'footer', fields },
    async (values) => {
      await onSubmit(values as Record<string, unknown>)
    },
    [onSubmit],
  )

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Form className="flex flex-col gap-4" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant={destructive ? 'destructive' : 'default'}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
